import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug, getOptimizedImageUrl } from '../lib/cms';
import { Plus, Minus } from 'lucide-react';

export default function About({ isEn }) {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaqIdx, setOpenFaqIdx] = useState(-1);

  useEffect(() => {
    async function loadData() {
      try {
        const page = await fetchPageBySlug('om');
        setPageData(page);
      } catch (error) {
        console.error('Error loading about page data:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const lang = isEn ? 'en' : 'da';

  const title = pageData?.title?.[lang] || (isEn ? 'About Us' : 'Om os');
  const seoTitle = pageData?.seo_title?.[lang] || `${title} - Fredericia Isolerings-puder`;
  const seoDesc = pageData?.seo_description?.[lang] || '';

  const faqItems = isEn ? [
    {
      q: "What are the advantages of insulation pads over traditional metal cladding?",
      a: "Insulation pads are flexible, easy to install and remove in minutes, significantly reducing downtime during service and maintenance. They also take up much less space during transport and can be reused indefinitely."
    },
    {
      q: "What temperatures can your insulation pads withstand?",
      a: "Our custom insulation pads can withstand extreme temperatures up to 1,300°C, making them ideal for process plants, turbines, exhaust systems, and power plants."
    },
    {
      q: "Are your products certified or approved?",
      a: "Yes, we supply marine-approved products that comply with strict international safety, fire-retardant, and heat-shielding requirements (including SOLAS standards for marine installations)."
    },
    {
      q: "How does measurement and production work?",
      a: "We manufacture 100% custom-made solutions based on precise measurements or 3D scans of your equipment. This ensures a perfect fit with no thermal bridges."
    },
    {
      q: "Are your insulation pads produced with green energy?",
      a: "Yes, since June 1, 2023, all our insulation pads are produced using 100% green electricity from our own solar power plant."
    }
  ] : [
    {
      q: "Hvad er fordelene ved isoleringspuder frem for traditionel metalindkapsling?",
      a: "Isoleringspuder er fleksible, nemme at montere og afmontere på få minutter, hvilket reducerer tidsforbrug ved service og vedligeholdelse markant. De fylder også langt mindre under transport og kan genbruges igen og igen."
    },
    {
      q: "Hvilke temperaturer kan jeres isoleringspuder tåle?",
      a: "Vores skræddersyede isoleringspuder kan modstå ekstreme temperaturer helt op til 1.300°C, hvilket gør dem ideelle til procesanlæg, turbiner, udstødninger og kraftværker."
    },
    {
      q: "Er jeres produkter certificerede eller godkendte?",
      a: "Ja, vi leverer marinegodkendte produkter, der overholder de strenge internationale krav til sikkerhed, brandhæmning og varmeafskærmning (herunder SOLAS-krav til marineinstallationer)."
    },
    {
      q: "Hvordan foregår opmåling og produktion?",
      a: "Vi producerer udelukkende skræddersyede løsninger efter præcise mål eller 3D-scanninger af jeres emner."
    },
    {
      q: "Produceres jeres isoleringspuder med grøn energi?",
      a: "Ja, siden den 1. juni 2023 produceres alle vores isoleringspuder udelukkende med 100% grøn strøm fra vores eget solcelleanlæg."
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
      </Helmet>

      {/* Main Content */}
      <section className="py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Block */}
          <div className="mb-12 text-left max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-black mb-4 tracking-tight">
              {title}
            </h1>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed">
              {isEn 
                ? 'At Fredericia Isoleringspuder ApS, we work every day to create sustainable solutions that make a difference for both our customers and the environment. We strive to deliver products of the highest quality, tailored to your specific needs.'
                : 'Hos Fredericia Isoleringspuder ApS arbejder vi hver dag på at skabe bæredygtige løsninger, der gør en forskel for både vores kunder og miljøet. Vi stræber efter at levere produkter af højeste kvalitet, der er skræddersyet til jeres specifikke behov.'}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* FAQ Accordion */}
              <div className="lg:col-span-7 space-y-4 text-left">
                <h2 className="text-2xl sm:text-3xl font-display font-bold text-black mb-6 tracking-tight">
                  {isEn ? 'Frequently Asked Questions' : 'Ofte stillede spørgsmål'}
                </h2>
                <div className="space-y-3">
                  {faqItems.map((item, idx) => {
                    const isOpen = openFaqIdx === idx;
                    return (
                      <div 
                        key={idx} 
                        className="bg-white border border-gray-200 rounded-none transition-all"
                      >
                        <button
                          onClick={() => setOpenFaqIdx(isOpen ? -1 : idx)}
                          className="w-full flex justify-between items-center p-5 text-left font-display font-bold text-gray-900 hover:text-brand-accent transition-colors uppercase text-sm sm:text-base tracking-wide"
                        >
                          <span>{item.q}</span>
                          <span className="shrink-0 ml-4 text-gray-400">
                            {isOpen ? <Minus className="h-4 w-4 text-brand-accent" /> : <Plus className="h-4 w-4" />}
                          </span>
                        </button>
                        {isOpen && (
                          <div className="p-5 pt-0 text-gray-600 text-sm sm:text-base leading-relaxed border-t border-gray-100 bg-gray-50/50">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Image */}
              <div className="lg:col-span-5 lg:sticky lg:top-28">
                <div className="relative aspect-[4/3] rounded-none overflow-hidden border border-gray-200 shadow-sm">
                  <img
                    src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/f9d0caaf-7221-46f9-967b-59f1f96503ae/fp-768x576-1.jpg', { width: 600 })}
                    alt="Fredericia Isoleringspuder"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
